/**
 * Profile Header Component
 *
 * Displays user avatar, name, email, premium status, and public profile link
 */

import { useAuthStore } from '@vsat/shared/store/authStore';
import { useTranslation } from 'react-i18next';

export function ProfileHeader() {
  const { user } = useAuthStore();
  const { t } = useTranslation();

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const fullName =
    user?.name ||
    (user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email?.split('@')[0] || t('account.header.role.user'));
  const email = user?.email || '';
  const role =
    user?.role === 'STUDENT'
      ? t('account.header.role.student')
      : user?.role || t('account.header.role.user');

  return (
    <section className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 shadow-sm'>
      <div className='flex flex-col md:flex-row items-start md:items-center justify-between gap-6'>
        <div className='flex items-center gap-5'>
          <div className='relative'>
            <div className='size-24 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 border-4 border-background-light dark:border-background-dark flex items-center justify-center text-orange-700 font-bold text-2xl ring-4 ring-background-light dark:ring-background-dark'>
              {getUserInitials()}
            </div>
            <button
              className='absolute bottom-0 right-0 flex size-8 items-center justify-center rounded-full bg-primary text-text-main shadow-md hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
              aria-label='Edit avatar'
            >
              <span className='material-symbols-outlined text-sm'>edit</span>
            </button>
          </div>
          <div className='flex flex-col'>
            <h2 className='text-xl font-bold text-gray-900 dark:text-white'>{fullName}</h2>
            <p className='text-gray-500 dark:text-gray-400 text-sm mb-1'>
              {role} - {email}
            </p>
          </div>
        </div>
        <button className='w-full md:w-auto px-5 py-2.5 bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-lg text-sm font-bold text-gray-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'>
          {t('account.header.viewPublicProfile')}
        </button>
      </div>
    </section>
  );
}
